import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled, { keyframes } from 'styled-components'

import { Column, Row } from '../Grid'
import { Text } from '../Text'
import { Checkbox } from '../Checkbox'
import { Chevron } from '../Icons'

import { useOuterClickNotifier, useHotkey } from '../../../hooks'

const MultiSelect = ({
  register,
  name,
  onChange,
  value,
  label,
  placeholder,
  options,
  error,
  disabled,
  icon,
  variant,
  ...props
}) => {
  const selectContainer = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  useHotkey('Escape', () => isOpen && setIsOpen(false))

  useEffect(() => {
    register && register({ name })
  }, [register, name])

  const handleClick = useCallback(
    newValue => {
      if ((value || []).includes(newValue)) {
        return onChange({ target: { value: value.filter(val => newValue !== val), name } })
      }

      return onChange({ target: { value: [...(value || []), newValue], name } })
    },
    [name, value]
  )

  const currentOption = useMemo(() => {
    const parsedValue = typeof value === 'string' ? [value] : value
    const optionSelected = (parsedValue || []).map(
      val => options.find(option => option.value === val)?.label
    )
    return !!optionSelected.length ? optionSelected.join(', ') : placeholder
  }, [options, value, placeholder])

  const hasOptionSelected = Boolean(currentOption)

  const hasValue = useCallback(
    currentValue => {
      return (value || []).includes(currentValue)
    },
    [value]
  )

  return (
    <Container width='100%' {...props}>
      <SelectBox
        type='button'
        disabled={disabled}
        onClick={() => setIsOpen(!isOpen)}
        error={error}
        ref={selectContainer}
      >
        <Row
          width='100%'
          p={icon && '12px 0px 12px 12px'}
          mt={variant === 'primary' ? '12px' : '0'}
          justifyContent='space-between'
        >
          <Row>
            {hasOptionSelected && variant !== 'primary' && (
              <Text fontSize='10px' position='absolute' top={0} color='gray.n400' as='label'>
                {variant === 'secondary' ? '' : label}
              </Text>
            )}
            {variant === 'primary' && (
              <Text fontSize='10px' position='absolute' top='2px' color='primary' as='label'>
                {label}
              </Text>
            )}
            <CurrentOption as='span'>{currentOption}</CurrentOption>
            {variant === 'primary' ? (
              <Text
                as='span'
                display={!hasOptionSelected ? 'auto' : 'none'}
                variant='small'
                color='gray.n400'
                mt='6px'
              >
                {placeholder}
              </Text>
            ) : (
              <Text
                as='span'
                display={!currentOption ? 'auto' : 'none'}
                variant={variant === 'secondary' ? 'small' : 'regular'}
                color='gray.n400'
              >
                {placeholder}
              </Text>
            )}
          </Row>
          {!disabled && (
            <Chevron
              size={20}
              color={icon ? '#7F8C8D' : '#FF7708'}
              variant={!isOpen ? 'down' : 'top'}
            />
          )}
        </Row>
      </SelectBox>
      {isOpen && (
        <OptionsContainer error={error}>
          <OptionList>
            {options.map(option => (
              <StyledOption
                key={option.value}
                selected={hasValue(option.value)}
                onClick={() => handleClick(option.value)}
                as='li'
                tabIndex='0'
                onKeyUp={event => {
                  if (event.key === 'Enter') {
                    handleClick(option.value)
                  }
                }}
              >
                <Checkbox
                  register={register}
                  value={hasValue(option.value)}
                  checked={hasValue(option.value)}
                  name={option.value}
                  label={option.label}
                  onClick={() => handleClick(option.value)}
                />
              </StyledOption>
            ))}
          </OptionList>
        </OptionsContainer>
      )}
      {error && !value && (
        <Text color='red' fontSize={11} textAlign='left' mt='5px'>
          {error}
        </Text>
      )}
    </Container>
  )
}

const CurrentOption = styled(Row)`
  position: absolute;
  color: black;
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: 86%;
`

const SelectBox = styled.button(
  ({ error, disabled, theme: { colors } }) => `
  overflow: hidden;
  text-align: left;
  display: flex;
  align-items: center;
  padding-left: 12px;
  width: 100%;
  height: 48px;
  color: ${disabled ? colors.gray.n300 : colors.black};
  position: relative;
  background-color: ${disabled ? colors.gray.n200 : colors.white};
  cursor: ${!disabled ? 'pointer' : 'not-allowed'};
  transition: all 0.3s ease;
  border-radius: 4px;
  border: 1px solid
    ${error ? colors.red : colors.gray.n300};
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
  &:focus {
    outline: none;
    border: 0.5px solid black;
  }
`
)

const OptionsContainer = styled.div(
  ({ error, theme: { colors } }) => `
  z-index: 2;
  max-height: 259px;
  position: absolute;
  width: 100%;
  background: ${colors.white};
  border: 1px solid ${colors.gray.n300};
  border-radius: 4px;
  overflow: auto;
  top: ${error ? 'calc(100% - 20px)' : '100%'};
  ::-webkit-scrollbar {
    width: 3px;
    background-color: ${colors.gray.n200};
  }
  ::-webkit-scrollbar-thumb {
    background-color: #babac0;
    border-radius: 16px;
  }
  ::-webkit-scrollbar-button {
    display: none;
  }
`
)

const isOpenMenu = keyframes`
  from {
    max-height: 0px;
  }
  to {
    max-height: 200px;
  }
`

const OptionList = styled.ul`
  animation: ${isOpenMenu} 0.3s ease;
  background-color: #fff;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
`
const Container = styled(Column)`
  position: relative;
`

const StyledOption = styled(Row)(
  ({ selected, theme: { colors } }) => `
  overflow: hidden;
  padding: 8px 16px;
  min-height: 36px;
  cursor: pointer;
  width: 100%;
  background: ${selected && `${colors.blue.n100} 0% 0% no-repeat padding-box`};
  &:hover {
    background: ${colors.blue.n100} 0% 0% no-repeat padding-box;
  }
  &:focus {
    outline: none;
  }
`
)

MultiSelect.defaultProps = {
  option: [],
  disabled: false
}

MultiSelect.prototype = {
  label: PropTypes.string,
  options: PropTypes.array.isRequired,
  register: PropTypes.func,
  error: PropTypes.error,
  name: PropTypes.string,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.string
}

export { MultiSelect }
